import { assert, isDefined } from '@sama/js-core';
import {
  isOrthographicVideoOutput,
  isSensorFusionOutput,
} from '../../../../cross-team-shared/misc-utils/output-utils';
import { AnnotationOutput } from '../../../../team-apis/otterdam/outputs/annotation-output';
import {
  AbstractOutput,
  Output,
} from '../../../../team-apis/otterdam/outputs/output';
import { SensorFusionOutput } from '../../../../team-apis/otterdam/outputs/sensor-fusion-output';
import { Project } from '../../../../team-apis/otterdam/project';
import { TaskWorkspaceStore } from '../../state/task-workspace-store';

export function getFusedOutputsObjectless(
  project: Project,
  fusionOutputName: string,
  displayOrder: string,
) {
  const fusionOutput = getOutputByName(project, fusionOutputName);
  assert(
    isSensorFusionOutput(fusionOutput),
    `${fusionOutputName} is not a sensor fusion output`,
  );

  const fusedOutputs = getFusedOutputsFromMetadata(project, fusionOutput);

  if (displayOrder) {
    return displayOrder
      .split(',')
      .map((item) => item.trim())
      .map((outputName) => getOutputByName(project, outputName));
  }

  return fusedOutputs;
}

export function getFusedOutputs(project: Project, displayOrder: string) {
  const fusionOutput = project.outputs.find(isSensorFusionOutput);
  assert(
    isDefined(fusionOutput),
    `object projects should always have a fusion output`,
  );

  const fusedOutputs = getFusedOutputsFromMetadata(project, fusionOutput);

  if (displayOrder) {
    return displayOrder
      .split(',')
      .map((item) => item.trim())
      .map((outputName) => getOutputByName(project, outputName));
  }

  return fusedOutputs;
}

export function getFusionOutput(project: Project, outputId: number) {
  const output = project.outputs.find((out) => out.id === outputId);
  if (output && isSensorFusionOutput(output)) {
    return output;
  }

  return project.outputs.find((out) => {
    if (!isSensorFusionOutput(out)) {
      return false;
    }

    return out.meta.fused_outputs.find(
      (fusedOutput) => fusedOutput.output_id === outputId,
    );
  }) as SensorFusionOutput | undefined;
}

export function getOutputByName(
  config: { outputs: Output[] },
  outputName: string,
) {
  const output = config.outputs.find((o) => o.name === outputName);
  assert(output, `Output ${outputName} does not exist`);
  return output;
}

export function getOutputById(config: { outputs: Output[] }, outputId: number) {
  const output = config.outputs.find((o) => o.id === outputId);
  assert(output, `Output ${outputId} does not exist`);
  return output;
}

export function isAnnotationOutput(
  output: AbstractOutput,
): output is AnnotationOutput {
  return (
    output.type === 'PointCloudOutput' ||
    output.type === 'VideoOutput' ||
    output.type === 'ImageAnnotationOutput'
  );
}

export function getFusedNonOrthographicVideoOutputs(
  project: Project,
  output: AnnotationOutput,
) {
  const fusionOutput = getFusionOutput(project, output.id);
  if (!fusionOutput) {
    return [];
  }

  return getFusedOutputsFromMetadata(project, fusionOutput).filter(
    (out) => !isOrthographicVideoOutput(out),
  );
}

export function hasFusedAnswerOptions(
  taskWorkspaceStore: TaskWorkspaceStore,
  annotationOutputId: number,
) {
  const fusionOutput = getFusionOutput(
    taskWorkspaceStore.state().project,
    annotationOutputId,
  );

  return fusionOutput ? fusionOutput.answer_options.length > 0 : false;
}

export function getFusedOutputsFromMetadata(
  project: Project,
  fusionOutput: SensorFusionOutput,
) {
  return fusionOutput.meta.fused_outputs
    .map((fusedOutput) => getOutputById(project, fusedOutput.output_id))
    .filter(isAnnotationOutput);
}

export function getFusedAnswerOptions(project: Project, outputName: string) {
  const output = getOutputByName(project, outputName);
  const fusionOutput = getFusionOutput(project, output.id);
  return fusionOutput ? fusionOutput.answer_options : [];
}

export function getSensorFusionOutput(outputs: AbstractOutput[]) {
  const fusionOutput = outputs.find(isSensorFusionOutput);
  assert(fusionOutput, 'FusionOutput must be defined when object is enabled');
  return fusionOutput;
}
